import { useCallback, useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { useAtom, useAtomValue, useSetAtom, type Atom, type PrimitiveAtom, type WritableAtom } from "jotai";
import { AutomationStepEditArea } from "@/routes/automations/automation-edit-page/AutomationStepEditArea";
import { AutomationStepList } from "@/components/automations/AutomationStepList";
import { AutomationEmptyStepCreator } from "@/routes/automations/automation-edit-page/AutomationEmptyStepCreator";
import { StepListContainer } from "@/routes/automations/automation-details-page/StepListContainer";
import { cn } from "@/components/podkit/lib/cn";
import { NewAutomationStepButton } from "@/routes/automations/automation-edit-page/NewAutomationStepButton";
import type { WorkflowEditFormStepData, WorkflowEditFormStepType } from "@/components/automations/automation-edit-form-data";
import type { DraftStep } from "@/routes/automations/automation-edit-page/types";

const STEP_EDIT_AREA_WIDTH = 480;

type AutomationDefinitionSectionEditProps = {
    editableStepsAtom: PrimitiveAtom<WorkflowEditFormStepData[] | null>;
    stepIndexBeingEditedAtom: PrimitiveAtom<number | null>;
    newStepAtom: PrimitiveAtom<WorkflowEditFormStepData | null>;
    draftStepAtom: PrimitiveAtom<DraftStep | null>;
    isCreatingStepAtom: Atom<boolean>;
    removeStepAtom: WritableAtom<null, [number], void>;
    createNewStepAtom: WritableAtom<null, [WorkflowEditFormStepType?], void>;
    createStepIfEmptyAtomEffect: Atom<void>;
    createDraftAtomEffect: Atom<void>;
    changeDraftStepTypeAtom: WritableAtom<null, [WorkflowEditFormStepType], void>;
    upsertDraftStepIntoStepsAtom: WritableAtom<null, [], boolean | undefined>;
};

export const AutomationDefinitionSectionEdit: FC<AutomationDefinitionSectionEditProps> = ({
    editableStepsAtom,
    stepIndexBeingEditedAtom,
    newStepAtom,
    draftStepAtom,
    isCreatingStepAtom,
    removeStepAtom,
    createNewStepAtom,
    createStepIfEmptyAtomEffect,
    createDraftAtomEffect,
    changeDraftStepTypeAtom,
    upsertDraftStepIntoStepsAtom,
}) => {
    useAtom(createStepIfEmptyAtomEffect);
    useAtom(createDraftAtomEffect);

    const [isClosingSidePanel, setIsClosingSidePanel] = useState(false);
    const editableSteps = useAtomValue(editableStepsAtom);
    const draftStep = useAtomValue(draftStepAtom);
    const selectedStepIndex = useAtomValue(stepIndexBeingEditedAtom);
    const isCreatingStep = useAtomValue(isCreatingStepAtom);
    const setStepIndexBeingEdited = useSetAtom(stepIndexBeingEditedAtom);
    const removeStep = useSetAtom(removeStepAtom);
    const setEditableSteps = useSetAtom(editableStepsAtom);
    const createNewStep = useSetAtom(createNewStepAtom);

    const isEditSidePanelVisible = draftStep !== null && !isClosingSidePanel;

    const onSidePanelCloseAnimationStart = useCallback(() => {
        setIsClosingSidePanel(true);
    }, []);

    const onSidePanelCloseAnimationEnd = useCallback(() => {
        setIsClosingSidePanel(false);
    }, []);

    const handleStepSelect = useCallback(
        (index: number) => {
            setStepIndexBeingEdited(index);
        },
        [setStepIndexBeingEdited],
    );

    const handleStepRemove = useCallback(
        (index: number) => {
            removeStep(index);
        },
        [removeStep],
    );

    const handleStepsReorder = useCallback(
        (newSteps: WorkflowEditFormStepData[]) => {
            setEditableSteps(newSteps);
            if (selectedStepIndex !== null) {
                const currentStep = editableSteps?.[selectedStepIndex];
                if (currentStep) {
                    const newIndex = newSteps.findIndex((s) => s.id === currentStep.id);
                    if (newIndex !== -1 && newIndex !== selectedStepIndex) {
                        setStepIndexBeingEdited(newIndex);
                    }
                }
            }
        },
        [setEditableSteps, selectedStepIndex, editableSteps, setStepIndexBeingEdited],
    );

    const handleAddStep = useCallback(() => {
        createNewStep();
    }, [createNewStep]);

    if (editableSteps === null) {
        return null;
    }

    let sideContent: ReactNode | null = null;
    if (draftStep === null && !isClosingSidePanel) {
        sideContent = null;
    } else {
        sideContent = (
            <AnimatedAutomationStepEditArea
                newStepAtom={newStepAtom}
                stepIndexBeingEditedAtom={stepIndexBeingEditedAtom}
                draftStepAtom={draftStepAtom}
                isCreatingStepAtom={isCreatingStepAtom}
                removeStepAtom={removeStepAtom}
                changeDraftStepTypeAtom={changeDraftStepTypeAtom}
                upsertDraftStepIntoStepsAtom={upsertDraftStepIntoStepsAtom}
                panelWidth={STEP_EDIT_AREA_WIDTH}
                onCloseAnimationStart={onSidePanelCloseAnimationStart}
                onCloseAnimationEnd={onSidePanelCloseAnimationEnd}
            />
        );
    }

    return (
        <div
            className={cn(
                "grid grow grid-rows-1 overflow-hidden border-t border-border-subtle",
                "ease-out-expo transition-all duration-150",
                isEditSidePanelVisible ? "grid-cols-[1fr_480px]" : "grid-cols-[1fr_0px]",
            )}
        >
            <StepListContainer className="flex-[3] grow rounded-none border-0">
                {editableSteps.length === 0 ? (
                    <AutomationEmptyStepCreator
                        draftStepAtom={draftStepAtom}
                        changeDraftStepTypeAtom={changeDraftStepTypeAtom}
                        upsertDraftStepIntoStepsAtom={upsertDraftStepIntoStepsAtom}
                    />
                ) : (
                    <div className="flex w-full max-w-screen-xl grow flex-row items-start justify-center gap-4">
                        <AutomationStepList
                            steps={editableSteps}
                            editable
                            selectedStepIndex={selectedStepIndex}
                            onStepSelect={handleStepSelect}
                            onStepRemove={handleStepRemove}
                            onStepsReorder={handleStepsReorder}
                            footer={
                                <NewAutomationStepButton isAddingStep={isCreatingStep} onAddStep={handleAddStep} />
                            }
                        />
                    </div>
                )}
            </StepListContainer>
            {sideContent}
        </div>
    );
};

type AnimatedAutomationStepEditAreaProps = {
    newStepAtom: PrimitiveAtom<WorkflowEditFormStepData | null>;
    stepIndexBeingEditedAtom: PrimitiveAtom<number | null>;
    draftStepAtom: PrimitiveAtom<DraftStep | null>;
    isCreatingStepAtom: Atom<boolean>;
    removeStepAtom: WritableAtom<null, [number], void>;
    changeDraftStepTypeAtom: WritableAtom<null, [WorkflowEditFormStepType], void>;
    upsertDraftStepIntoStepsAtom: WritableAtom<null, [], boolean | undefined>;
    panelWidth: number;
    onCloseAnimationStart: () => void;
    onCloseAnimationEnd: () => void;
};

const AnimatedAutomationStepEditArea: FC<AnimatedAutomationStepEditAreaProps> = ({
    newStepAtom,
    stepIndexBeingEditedAtom,
    draftStepAtom,
    isCreatingStepAtom,
    removeStepAtom,
    changeDraftStepTypeAtom,
    upsertDraftStepIntoStepsAtom,
    panelWidth,
    onCloseAnimationStart,
    onCloseAnimationEnd,
}) => {
    const editAreaRef = useRef<HTMLDivElement>(null);
    const startTime = useRef(-1);
    const endTime = useRef(-1);
    const setNewStep = useSetAtom(newStepAtom);
    const setStepIndexBeingEdited = useSetAtom(stepIndexBeingEditedAtom);
    const animationRequestIdRef = useRef<number | null>(null);

    const end = panelWidth + 100;

    const playAnimation = useCallback(
        ({ reverse = false }: { reverse?: boolean } = {}) =>
            new Promise<void>((resolve) => {
                const nextFrame = (timestamp: number) => {
                    if (!editAreaRef.current) return;

                    if (startTime.current === -1) {
                        if (reverse) {
                            onCloseAnimationStart();
                        }
                        startTime.current = timestamp;
                        endTime.current = timestamp + 400;
                    }

                    const t = (timestamp - startTime.current) / (endTime.current - startTime.current);
                    const p = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
                    const progress = end * p;
                    const x = reverse ? progress : end - progress;

                    editAreaRef.current.style.transform = `translateX(${x}px)`;

                    if (t < 1) {
                        animationRequestIdRef.current = requestAnimationFrame(nextFrame);
                    } else {
                        startTime.current = -1;
                        endTime.current = -1;
                        onCloseAnimationEnd();
                        resolve();
                    }
                };
                animationRequestIdRef.current = requestAnimationFrame(nextFrame);
            }),
        [end, onCloseAnimationStart, onCloseAnimationEnd],
    );

    useEffect(() => {
        void playAnimation();
        return () => {
            if (animationRequestIdRef.current) {
                cancelAnimationFrame(animationRequestIdRef.current);
                animationRequestIdRef.current = null;
            }
        };
    }, [playAnimation]);

    const closeSidePanel = async () => {
        await playAnimation({ reverse: true });
        setNewStep(null);
        setStepIndexBeingEdited(null);
    };

    return (
        <AutomationStepEditArea
            ref={editAreaRef}
            stepIndexBeingEditedAtom={stepIndexBeingEditedAtom}
            draftStepAtom={draftStepAtom}
            isCreatingStepAtom={isCreatingStepAtom}
            removeStepAtom={removeStepAtom}
            changeDraftStepTypeAtom={changeDraftStepTypeAtom}
            upsertDraftStepIntoStepsAtom={upsertDraftStepIntoStepsAtom}
            onClose={closeSidePanel}
        />
    );
};
